import React, { Component, useEffect, useState } from 'react'
import { result, first, last, reduce, find } from 'libraries/lodash'
import cx from 'classnames'
import Slider from '@mui/material/Slider'
import countryCoordinates from 'constants/mw-analytics-country-coordinates'
import GOOGLE_API_KEY from 'constants/google-charts-api-key'
import { withResizeDetector } from 'react-resize-detector'
import PanelTitle from 'pages/mwanalytics/PanelTitle'

export default class LocationMap extends Component {
  state = {
    dateIndex: 0
  }

  constructor () {
    super(...arguments)
    const dateIndex = result(this.dataProp, [0, 'plot'], [0]).length - 1
    this.state = { dateIndex }
    this.mapid = Date.now().toString(36)
  }

  componentDidMount () {
    this.loadGoogleChart()
  }

  componentDidUpdate () {
    this.drawRegionsMap()
  }

  loadGoogleChart = () => {
    window.google.charts.load('current', {
      packages: ['geochart'],
      mapsApiKey: GOOGLE_API_KEY
    })
    window.google.charts.setOnLoadCallback(this.drawRegionsMap)
  }

  drawRegionsMap = () => {
    if (!this.chart) {
      this.chart = new window.google.visualization.GeoChart(document.getElementById(this.mapid))
    }

    const data = window.google.visualization.arrayToDataTable([
      ['Country', ''],
      ...this.data
    ])

    this.chart.draw(data, {
      colorAxis: { colors: ['#C5D7F6', '#4374e0'] }
    })
  }

  get dataProp () {
    return this.props.stats.data.filter(({ name }) => !!name)
  }

  get data () {
    return this.dataProp
      .map(({ name, plot }) => [this.getCountryName(name), plot[this.state.dateIndex].y])
      .filter(([label, value]) => !!value)
  }

  get dates () {
    return result(this.dataProp, [0, 'plot'], []).map(({ x }) => x)
  }

  get displayValues () {
    const { dateIndex } = this.state
    return this.data.map(({ name, label, plot }) => ({
      name,
      ...countryCoordinates[name],
      value: plot[dateIndex].y
    })).filter(({ name, value }) => name && value)
  }

  getCountryName (name) {
    const { parameterName, documentConfiguration } = this.props
    const parameterConfig = reduce(documentConfiguration, (result, x) => result || x.parameters[parameterName], null)
    const restrictingValues = result(parameterConfig, 'restrictingValues', [])
    return result(find(restrictingValues, x => x.value === name), 'displayName', name)
  }

  updateViewport = (viewport) => {
    this.setState({ viewport })
  }

  handleChange = (event, dateIndex) => {
    event.preventDefault()
    this.setState({ dateIndex })
  }

  render () {
    const { dates, props: { displayName } } = this
    const { dateIndex } = this.state

    return (
      <>
        <PanelTitle {...{ displayName }} />
        {dates.length > 1 && (
          <div className='flex items-center mb6'>
            <span className='pa0 fw4 grey f5 nowrap mr6'>{first(dates)}</span>
            <Slider
              min={0}
              max={dates.length - 1}
              step={1}
              value={dateIndex}
              onChange={this.handleChange}
              track={false}
            />
            <span className='pa0 fw4 grey f5 nowrap ml6'>{last(dates)}</span>
          </div>
        )}
        <div className='relative min-h'>
          <ResizeGeoMap
            mapId={this.mapid}
            loadGoogleChart={this.loadGoogleChart}
            data={this.data}
          />
          {dates.length > 1 && (
            <div className='absolute top4 left4 bg-black white pv2 ph4'>
              {dates[dateIndex]}
            </div>
          )}
        </div>
      </>
    )
  }
}

const ResizeGeoMap = withResizeDetector(({ mapId, loadGoogleChart, width }) => {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)

    const timerId = setTimeout(() => {
      loadGoogleChart()
      setIsLoading(false)
    }, 500)

    return () => clearTimeout(timerId)
  }, [width, loadGoogleChart, setIsLoading])

  return (
    <div
      className='flex items-center justify-center w-100'
      style={{ minHeight: 400 }}
    >
      <div
        id={mapId}
        className={cx('relative w-100', { dn: isLoading })}
        style={{ minHeight: 400, maxWidth: width ? width * 0.96 : 'initial' }}
      />
      <span className={cx({ dn: !isLoading })}>
        Loading map...
      </span>
    </div>
  )
})
